import { Container, Graphics } from "pixi.js"
import type { GridLineKind, MeasureGrid } from "./grid-lines";

type MeasuresProps = {
  fromTick: number;
  toTick: number;
  width: number;
  height: number;
  highlightActive: boolean;
}

/**
 * How strongly the beat grid reads when it is switched on.
 *
 * On by default only when the player asked for the metronome — and, since the
 * refonte of the step in time, for the length of a pass, where it *is* the
 * visible pulse. So it has to carry meaning, and 0.22 / 0.18 on the roll's
 * ground (#0a0a10) came to 1.9:1 and 1.7:1, under the 3:1 WCAG 1.4.11 asks of
 * a graphic that does. These put the bar line at ~3.6:1 and the beat line at
 * 3.0:1; the dashed subdivision stays deliberately below it, being texture
 * rather than information.
 */
const BAR_LINE_ALPHA = 0.4;
const BEAT_LINE_ALPHA = 0.34;

const DASH_LENGTH = 4;
const DASH_GAP = 4;

/**
 * La trame ne numérote pas ses temps.
 *
 * Elle l'a fait — « 1 2 3 4 » le long du bord gauche, sous le métronome — et
 * c'était le seul endroit qui disait où l'on en était dans la mesure. La règle
 * graduée tient ce bord depuis, et elle y écrit ses numéros de mesure : deux
 * colonnes de chiffres à quelques pixels l'une de l'autre, qui ne comptent pas
 * la même chose, se lisent moins bien qu'une seule.
 */

/**
 * Un bassin de `Graphics` par espèce de ligne.
 *
 * Avec un seul bassin indexé dans l'ordre d'acquisition, chaque franchissement
 * d'une graduation par `fromTick` décalait toute la séquence d'un cran —
 * `bar, subdiv, beat…` devenait `subdiv, beat, subdiv…` — et toutes les
 * lignes visibles étaient retessellées, quatre fois par seconde à 120 BPM,
 * pointillés compris. Ici une ligne ne change jamais d'espèce : seule une
 * largeur ou une bascule de surbrillance la redessine.
 */
type LinePool = {
  graphics: Graphics[];
  width: number[];
  index: number;
  highWaterMark: number;
};

function createLinePool(): LinePool {
  return { graphics: [], width: [], index: 0, highWaterMark: 0 };
}

export class Measures {
  private container: Container;

  private previousFromTick = NaN;
  private previousToTick = NaN;
  private previousWidthSig = -1;
  private previousHeight = -1;
  private previousHighlightActive = false;
  private hasPreviousProps = false;
  private linesVisible = true;

  private readonly pools: Record<GridLineKind, LinePool> = {
    bar: createLinePool(),
    beat: createLinePool(),
    subdiv: createLinePool(),
  };
  private readonly poolList: readonly LinePool[] = [this.pools.bar, this.pools.beat, this.pools.subdiv];

  private framesSinceLastShrink = 0;

  constructor(private stage: Container, private grid: MeasureGrid) {
    this.container = new Container();

    this.stage.addChild(this.container);
  }

  /** Repeindre la prochaine image même si les props n'ont pas bougé. */
  public invalidate(): void {
    this.hasPreviousProps = false;
  }

  /**
   * Le joueur a masqué les lignes de mesure.
   *
   * Sauf quand la trame est allumée (`highlightActive`) : le métronome ou la
   * passe de rythme l'ont demandée, et elle y est la pulsation visible — la
   * masquer retirerait l'exercice, pas un décor.
   */
  public setLinesVisible(visible: boolean): void {
    this.linesVisible = visible;
    this.invalidate();
  }

  public destroy(): void {
    for (const pool of this.eachPool()) {
      for (const g of pool.graphics) {
        g.destroy();
      }

      pool.graphics.length = 0;
      pool.width.length = 0;
      pool.index = 0;
    }

    this.container.destroy({ children: true });
  }

  public draw(props: MeasuresProps): void {
    if (this.shouldSkip(props)) {
      return;
    }

    this.container.visible = this.linesVisible || props.highlightActive;

    const lines = this.grid.linesFor(props.fromTick, props.toTick);
    const tickRange = props.toTick - props.fromTick;
    const scale = props.height / tickRange;
    // Stroke style depends on highlightActive, so a flip must invalidate
    // all pooled Graphics, width alone doesn't change.
    const highlightActiveChanged = this.hasPreviousProps
      && this.previousHighlightActive !== props.highlightActive;

    this.resetPools();

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];

      if (line.tick < props.fromTick || line.tick > props.toTick) {
        continue;
      }

      if (line.kind === 'subdiv' && !props.highlightActive) {
        continue;
      }

      const y = props.height - (line.tick - props.fromTick) * scale;
      const pool = this.pools[line.kind];
      const poolIdx = pool.index;
      const g = this.getOrCreateLine(pool);
      const widthChanged = pool.width[poolIdx] !== props.width;

      if (widthChanged || highlightActiveChanged) {
        this.drawLine(g, line.kind, props.width, y, props.highlightActive);
        pool.width[poolIdx] = props.width;
      } else {
        g.y = y;
        g.visible = true;
      }

      g.alpha = 1;
    }

    this.previousFromTick = props.fromTick;
    this.previousToTick = props.toTick;
    this.previousWidthSig = props.width;
    this.previousHeight = props.height;
    this.previousHighlightActive = props.highlightActive;
    this.hasPreviousProps = true;

    for (const pool of this.eachPool()) {
      if (pool.index > pool.highWaterMark) {
        pool.highWaterMark = pool.index;
      }
    }

    this.framesSinceLastShrink++;

    if (this.framesSinceLastShrink >= 300) {
      this.shrinkPools();
      this.framesSinceLastShrink = 0;
    }
  }

  private shouldSkip(props: MeasuresProps): boolean {
    if (!this.hasPreviousProps) {
      return false;
    }

    return this.previousFromTick === props.fromTick
      && this.previousToTick === props.toTick
      && this.previousWidthSig === props.width
      && this.previousHeight === props.height
      && this.previousHighlightActive === props.highlightActive;
  }

  private drawLine(g: Graphics, kind: GridLineKind, width: number, y: number, highlightActive: boolean): void {
    g.clear();

    if (kind === 'bar') {
      g.moveTo(0, 0);
      g.lineTo(width, 0);

      if (highlightActive) {
        g.stroke({ color: 0xffffff, alpha: BAR_LINE_ALPHA, width: 2 });
      } else {
        g.stroke({ color: 0xffffff, alpha: 0.12, width: 2 });
      }
    } else if (kind === 'beat') {
      g.moveTo(0, 0);
      g.lineTo(width, 0);

      if (highlightActive) {
        g.stroke({ color: 0xffffff, alpha: BEAT_LINE_ALPHA, width: 1 });
      } else {
        g.stroke({ color: 0xffffff, alpha: 0.06, width: 1 });
      }
    } else {
      const step = DASH_LENGTH + DASH_GAP;

      for (let x = 0; x < width; x += step) {
        const end = Math.min(x + DASH_LENGTH, width);
        g.moveTo(x, 0);
        g.lineTo(end, 0);
      }

      g.stroke({ color: 0xffffff, alpha: 0.06, width: 1 });
    }

    g.x = 0;
    g.y = y;
    g.visible = true;
  }

  private eachPool(): readonly LinePool[] {
    return this.poolList;
  }

  private resetPools(): void {
    for (const pool of this.eachPool()) {
      for (let i = 0; i < pool.index; i++) {
        pool.graphics[i].visible = false;
      }

      pool.index = 0;
    }
  }

  private shrinkPools(): void {
    for (const pool of this.eachPool()) {
      const targetSize = Math.ceil(pool.highWaterMark * 1.2);

      if (pool.graphics.length > targetSize) {
        for (let i = targetSize; i < pool.graphics.length; i++) {
          pool.graphics[i].destroy();
        }

        pool.graphics.length = targetSize;
        pool.width.length = targetSize;
      }

      pool.highWaterMark = 0;
    }
  }

  private getOrCreateLine(pool: LinePool): Graphics {
    if (pool.index < pool.graphics.length) {
      return pool.graphics[pool.index++];
    }

    const g = new Graphics();
    this.container.addChild(g);
    pool.graphics.push(g);
    pool.index++;

    return g;
  }
}
